import { devLog, devWarn, devError } from '../utils/devLogger';

// 구글 로그인 SDK 초기화 및 유틸리티 함수
declare global {
  interface Window {
    google: any;
    gapi: any;
    isFlutterApp?: boolean;
    FlutterBridge?: {
      postMessage?: (message: string) => void;
    };
  }
}

const GOOGLE_GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const GOOGLE_SDK_TIMEOUT_MS = 10000;

let googleSdkLoadPromise: Promise<void> | null = null;

const isGoogleSdkReady = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return !!window.google?.accounts?.oauth2;
};

const waitForGoogleSdkReady = (timeoutMs: number = GOOGLE_SDK_TIMEOUT_MS): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (isGoogleSdkReady()) {
      resolve();
      return;
    }

    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      if (isGoogleSdkReady()) {
        window.clearInterval(intervalId);
        resolve();
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        window.clearInterval(intervalId);
        reject(new Error('구글 SDK 로드 대기 시간이 초과되었습니다.'));
      }
    }, 100);
  });
};

const createGoogleSdkLoadPromise = (timeoutMs: number): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (isGoogleSdkReady()) {
      resolve();
      return;
    }

    const existingScript = document.querySelector(`script[src="${GOOGLE_GSI_SCRIPT_SRC}"]`) as HTMLScriptElement | null;
    const script = existingScript ?? document.createElement('script');
    let settled = false;

    const cleanup = () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };

    const finishResolve = () => {
      if (settled) {
        return;
      }

      settled = true;
      cleanup();
      resolve();
    };

    const finishReject = (error: Error) => {
      if (settled) {
        return;
      }

      settled = true;
      cleanup();
      reject(error);
    };

    const handleLoad = () => {
      waitForGoogleSdkReady(Math.min(3000, timeoutMs))
        .then(finishResolve)
        .catch(() => finishReject(new Error('구글 SDK readiness 확인 실패')));
    };

    const handleError = () => {
      finishReject(new Error('구글 SDK 스크립트 로드에 실패했습니다.'));
    };

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);

    if (!existingScript) {
      script.src = GOOGLE_GSI_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    waitForGoogleSdkReady(timeoutMs).then(finishResolve).catch(finishReject);
  });
};

const ensureGoogleSdkLoaded = async (): Promise<void> => {
  if (typeof window === 'undefined') {
    throw new Error('브라우저 환경에서만 구글 SDK를 로드할 수 있습니다.');
  }

  if (isGoogleSdkReady()) {
    return;
  }

  if (!googleSdkLoadPromise) {
    googleSdkLoadPromise = createGoogleSdkLoadPromise(GOOGLE_SDK_TIMEOUT_MS);
  }

  try {
    await googleSdkLoadPromise;
  } catch (error) {
    googleSdkLoadPromise = null;
    throw error;
  }
};

// 구글 SDK 초기화
export const initGoogleSDK = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    
    if (!clientId || clientId === 'your-google-client-id.apps.googleusercontent.com') {
      devError('구글 클라이언트 ID가 설정되지 않았습니다. 환경변수를 확인해주세요.');
      return false;
    }

    if (!isGoogleSdkReady()) {
      void ensureGoogleSdkLoaded().catch((error) => {
        devWarn('구글 SDK 선로드 실패:', error);
      });

      devWarn('구글 SDK가 아직 준비되지 않아 로드를 시도합니다.');
      return false;
    }

    devLog('구글 SDK 초기화 완료', { clientId });
    return true;
    
  } catch (error) {
    devError('구글 SDK 초기화 실패:', error);
    return false;
  }
};

// 구글 로그인
export const googleLogin = async (): Promise<any> => {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  if (!clientId || clientId === 'your-google-client-id.apps.googleusercontent.com') {
    throw new Error('구글 클라이언트 ID가 설정되지 않았습니다. 환경변수를 확인해주세요.');
  }

  await ensureGoogleSdkLoaded();

  return new Promise((resolve, reject) => {
    try {
      if (!window.google?.accounts?.oauth2) {
        reject(new Error('구글 SDK가 로드되지 않았습니다.'));
        return;
      }

      devLog('구글 OAuth2 팝업 로그인 시작...');

      // OAuth2 팝업 방식으로 직접 로그인 (FedCM 경고 방지)
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: 'email profile openid',
        callback: (response: any) => {
          devLog('구글 OAuth2 응답:', response);
          
          if (response.access_token) {
            // 사용자 정보 가져오기
            fetch(`https://www.googleapis.com/oauth2/v2/userinfo?access_token=${response.access_token}`)
              .then(res => {
                if (!res.ok) {
                  throw new Error(`HTTP error! status: ${res.status}`);
                }
                return res.json();
              })
              .then(userInfo => {
                devLog('구글 사용자 정보:', userInfo);
                
                const googleData = {
                  accessToken: response.access_token,
                  userInfo: userInfo,
                  providerId: userInfo.id
                };
                
                devLog('구글 로그인 성공:', googleData);
                resolve(googleData);
              })
              .catch(error => {
                devError('구글 사용자 정보 조회 실패:', error);
                reject(new Error('구글 사용자 정보 조회에 실패했습니다.'));
              });
          } else if (response.error) {
            devError('구글 OAuth2 오류:', response.error);
            if (response.error === 'popup_closed_by_user') {
              reject(new Error('사용자가 로그인을 취소했습니다.'));
            } else {
              reject(new Error(`구글 로그인 오류: ${response.error}`));
            }
          } else {
            reject(new Error('구글 액세스 토큰을 받지 못했습니다.'));
          }
        },
        error_callback: (error: any) => {
          devError('구글 OAuth2 에러:', error);
          reject(new Error('구글 로그인 중 오류가 발생했습니다.'));
        }
      });

      // 팝업 방식으로 로그인 요청
      tokenClient.requestAccessToken();

    } catch (error) {
      devError('구글 로그인 실행 중 오류:', error);
      reject(error);
    }
  });
};

// 구글 로그아웃
export const googleLogout = (): Promise<void> => {
  return new Promise((resolve) => {
    try {
      if (window.google && window.google.accounts) {
        // OAuth2 토큰 해제
        window.google.accounts.oauth2.revoke && window.google.accounts.oauth2.revoke();
        devLog('구글 로그아웃 완료');
      }
    } catch (error) {
      devLog('구글 로그아웃 처리 중 오류:', error);
    }
    resolve();
  });
};
